import type { CategoryId } from "@/services/collection/categories";
import { CategorySelect } from "@/services/collection/categories";
import type { ItemStatus } from "@/services/collection/item";
import { ItemStatusUrlString, UrlStringToItemStatus } from "@/services/collection";
import { type OnlyPublicProperties } from "@/types";
import { getEnumKeyByValue, getUrlParamAsString, getUrlQueryAsNumber, getUrlQueryAsString, isStringInEnum } from "@/utilities";
import type { InjectionKey, Ref } from "vue";

export type injectionKeyType = Ref<OnlyPublicProperties<Filter>>;
export const injectionKey = Symbol() as InjectionKey<injectionKeyType>;

type CategoryValue = CategoryId | CategorySelect.CategoriesAll;
type ItemStatusValue = ItemStatus | undefined;

type Model = {
  fulltext: string;
  categoryId: CategoryValue;
  itemStatus: ItemStatusValue;
};

/** @deprecated Try to not use this anymore. This will be dropped, when collection stats will be in new endpoints. */
export class Filter {
  private _categoryId: CategoryValue;
  private _itemStatus: ItemStatusValue;
  private _fulltext: string;

  // Preset/default values
  private defaults: Model;

  // What to set when reset() is done
  private emptyState: Model;

  constructor(customDefaults: Partial<Model> = {}, emptyState: Partial<Model> = {}) {
    const defaults = {
      categoryId: CategorySelect.ValueAllCategories,
      itemStatus: undefined,
      fulltext: "",
    };

    this.defaults = {
      ...defaults,
      ...customDefaults,
    };

    const receivedEmptyStateWithoutUndefined = Object.fromEntries(Object.entries(emptyState).filter(([_, value]) => value !== undefined));
    this.emptyState = {
      ...defaults,
      ...receivedEmptyStateWithoutUndefined,
    };

    this.reset();
  }

  public fillFromUrl(): Filter {
    this.reset();

    try {
      this.categoryId = getUrlQueryAsNumber("categoryId");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    try {
      const urlItemStatus = getUrlParamAsString("itemStatus");
      if (isStringInEnum(urlItemStatus, ItemStatusUrlString)) {
        const enumString = getEnumKeyByValue(urlItemStatus, ItemStatusUrlString);
        this.itemStatus = UrlStringToItemStatus[enumString];
      }
    } catch (e: unknown) { } /* eslint-disable-line */ // Keep value set in reset() call

    try {
      this.fulltext = getUrlQueryAsString("fulltext");
    } catch (e: unknown) {} /* eslint-disable-line */ // Keep value set in reset() call

    return this;
  }

  public createApiParams(): Record<string, string | number | number[] | undefined> {
    return {
      categoryId: this.categoryId !== CategorySelect.ValueAllCategories ? this.categoryId : undefined,
      itemStatus: this.itemStatus,
      fulltext: this.fulltext,
    };
  }

  public createUrlQuery(): Record<string, string | number | number[] | undefined> {
    const params = this.createApiParams(); // DRY
    params.itemStatus = undefined; // ItemStatus is not in query, it's URL parameter.
    return params;
  }

  public reset(): void {
    this.fulltext = this.defaults.fulltext;
    this.categoryId = this.defaults.categoryId;
    this.itemStatus = this.defaults.itemStatus;
  }

  public set fulltext(newValue: string) {
    this._fulltext = newValue;
  }

  public get fulltext(): string {
    return this._fulltext;
  }

  public set categoryId(newCategoryId: CategoryValue) {
    this._categoryId = newCategoryId;
  }

  public get categoryId(): CategoryValue {
    return this._categoryId;
  }

  public get itemStatus(): ItemStatusValue {
    return this._itemStatus;
  }

  public set itemStatus(wantedItemStatus: ItemStatusValue) {
    this._itemStatus = wantedItemStatus;
    this.defaults.itemStatus = wantedItemStatus; // Current ItemStatus is always default - ignored in reset()
  }
}
